import React, { useEffect, useEffectEvent, useMemo, useState } from "react";

import { Spinner, useWizard } from "@performyard/py-components";

import { useCreateUserSelection, useUserSelectionCount } from "../../api/hooks/defs/user-selection";
import { useFeatureFlagQuery } from "../../hooks/useFeatureFlag";

import EmployeeSelection from "../EmployeeSelection";
import { WizardStepHeader } from "../WizardStep";

type WizardSubjectSelectionProps = {
	/** Wizard title, rendered above the step subtitle. */
	stepTitle: string;
	/** Step subtitle, e.g. "Add Subjects". */
	subtitle: string;
	/** Step id the selection id + count are published under in `wizardData`. */
	stepId: string;
	userSelectionId: string | null;
	onSelectionCreated: (id: string) => void;
	/** Rendered above the table when the step's own gate has failed. */
	error?: React.ReactNode;
	filters?: Record<string, string | string[]>;
	mutationFilters?: { canAssignGoalsTo?: boolean; canAssignReviewsTo?: boolean };
	showGroupsButton?: boolean;
};

const emptyExclusionList: string[] = [];
const emptyFilters: Record<string, string | string[]> = {};

/**
 * The subject-picking step shared by the wizard flows. Selection state lives
 * server-side in a user-selection object (created here on first mount); the wizard only
 * carries its id and the selected count, published to `wizardData` under `stepId`.
 */
export const WizardSubjectSelection = ({
	stepTitle,
	subtitle,
	stepId,
	userSelectionId,
	onSelectionCreated,
	error,
	filters = emptyFilters,
	mutationFilters,
	showGroupsButton = true,
}: WizardSubjectSelectionProps) => {
	const { setStepData, setNextDisabled } = useWizard();

	const createSelection = useCreateUserSelection({
		onSuccess: (data) => onSelectionCreated(data.user_selection_id),
	});

	const initializeSelection = useEffectEvent(() => {
		if (!userSelectionId && !createSelection.isPending) {
			createSelection.mutate();
		}
	});

	useEffect(() => {
		initializeSelection();
	}, []);

	const { data: selectionCountData } = useUserSelectionCount(userSelectionId ?? "");
	const selectedCount = selectionCountData?.count ?? 0;

	useEffect(() => {
		if (userSelectionId) {
			setStepData(stepId, { userSelectionId, selectedCount });
		}
	}, [stepId, userSelectionId, selectedCount, setStepData]);

	const isInitializing = !userSelectionId;
	const [isTableBusy, setIsTableBusy] = useState(false);

	useEffect(() => {
		setNextDisabled(isInitializing || isTableBusy);
		return () => setNextDisabled(false);
	}, [isInitializing, isTableBusy, setNextDisabled]);

	const { value: isGroupsEnabled } = useFeatureFlagQuery<boolean>("ff-is-groups-enabled", false);

	const customFieldsIdentifiers = useMemo(() => {
		const fields = ["Hire Date", "Manager"];
		if (isGroupsEnabled) fields.unshift("Group");
		return fields;
	}, [isGroupsEnabled]);

	if (isInitializing) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="flex flex-col items-center">
			<WizardStepHeader title={stepTitle} subtitle={subtitle} />
			<div className="mt-8 flex w-full max-w-[1096px] flex-col gap-4 px-4">
				{error}
				<EmployeeSelection
					selectionObjectID={userSelectionId}
					customFieldsIdentifiers={customFieldsIdentifiers}
					exclusionList={emptyExclusionList}
					filters={filters}
					mutationFilters={mutationFilters}
					paginationAlignment="end"
					onlySelected={false}
					showGroupsButton={showGroupsButton}
					onMutatingChange={setIsTableBusy}
					height="calc(100dvh - 350px)"
				/>
			</div>
		</div>
	);
};
